import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import {
  MessageSquare,
  Gamepad2,
  Home,
  Shield,
  Heart,
  Globe,
  ArrowRight,
  Check,
  Star,
  Users,
  Clock,
  Accessibility,
  Hand,
  Mic,
  Eye,
  MousePointer,
  Scan,
  Smile,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 },
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const features = [
  {
    icon: MessageSquare,
    title: "AAC Communication",
    description:
      "Express yourself freely with customizable communication boards, text-to-speech, and AI-assisted conversation.",
  },
  {
    icon: Accessibility,
    title: "Your Way to Control",
    description:
      "Choose from eye tracking, head movements, switches, voice, or scanning — use whatever works best for you.",
  },
  {
    icon: Gamepad2,
    title: "Entertainment",
    description:
      "Access YouTube, Spotify, games, and more. Stay entertained and connected to the world around you.",
  },
  {
    icon: Home,
    title: "Smart Home Control",
    description:
      "Control your environment - lights, TV, thermostat, and more - independently and effortlessly.",
  },
  {
    icon: Shield,
    title: "Emergency Alerts",
    description:
      "Instant caregiver notifications and emergency alerts for peace of mind, 24/7.",
  },
  {
    icon: Globe,
    title: "Multi-Language",
    description:
      "Full support for English, Hebrew, Arabic, and Russian with more languages coming soon.",
  },
];

const inputMethods = [
  { name: "Eye Tracking", description: "Control with your gaze", icon: Eye },
  { name: "Head Tracking", description: "Use head movements", icon: MousePointer },
  { name: "External Switches", description: "Adaptive switch support", icon: Hand },
  { name: "Voice Control", description: "Speak to navigate", icon: Mic },
  { name: "Mouth Gestures", description: "Facial expressions", icon: Smile },
  { name: "Scanning", description: "Automatic traversal", icon: Scan },
];

const benefits = [
  "No monthly subscription fees for core features",
  "Works on any modern tablet or computer",
  "Adapts to your unique abilities",
  "Regular updates with new features",
  "Secure and private - your data stays yours",
  "Responsive support team",
];

export default function Landing() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      <Helmet>
        <title>EyeControl App - Accessible Communication &amp; Control Platform | Independence for Everyone</title>
        <meta
          name="description"
          content="EyeControl App empowers people with motor impairments to communicate, control their environment, and stay entertained using the input method that works best for them."
        />
      </Helmet>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <img
                src="/lovable-uploads/27b5c758-8df7-4797-90b4-1fa139f668f7.png"
                alt="EyeControl App Logo"
                className="h-8 w-auto"
              />
              <span className="text-xl font-bold text-primary">EyeControl App</span>
            </div>
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate("/guide")}
                className="hidden sm:inline-flex"
              >
                About
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate("/pricing")}
                className="hidden sm:inline-flex"
              >
                Pricing
              </Button>
              <Button onClick={() => navigate("/auth")}>
                Get Started
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent pointer-events-none" />
        <motion.div
          className="max-w-5xl mx-auto text-center relative z-10"
          initial="initial"
          animate="animate"
          variants={staggerContainer}
        >
          <motion.div
            variants={fadeInUp}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary mb-6"
          >
            <Accessibility className="h-4 w-4" />
            <span className="text-sm font-medium">Assistive Technology for Everyone</span>
          </motion.div>

          <motion.h1
            variants={fadeInUp}
            className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6"
          >
            Communication &amp; Independence
            <span className="text-primary block mt-2">For Everyone</span>
          </motion.h1>

          <motion.p
            variants={fadeInUp}
            className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto mb-10"
          >
            EyeControl App is an accessible platform that empowers people with motor impairments to
            communicate, control their environment, and stay entertained — using whatever input
            method works best for them.
          </motion.p>

          <motion.div variants={fadeInUp} className="flex flex-wrap justify-center gap-4">
            <Button size="lg" onClick={() => navigate("/auth")} className="text-lg px-8">
              Start Free Trial
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate("/guide")}
              className="text-lg px-8"
            >
              Learn More
            </Button>
          </motion.div>

          <motion.div
            variants={fadeInUp}
            className="mt-12 flex flex-wrap justify-center gap-8 text-sm text-muted-foreground"
          >
            <div className="flex items-center gap-2">
              <Check className="h-5 w-5 text-green-500" />
              <span>No credit card required</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="h-5 w-5 text-green-500" />
              <span>7-day free trial</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="h-5 w-5 text-green-500" />
              <span>Cancel anytime</span>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* Features Grid */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Everything You Need, <span className="text-primary">One Platform</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              From communication to entertainment, EyeControl App provides a complete solution for
              independent living.
            </p>
          </motion.div>

          <motion.div
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                variants={fadeInUp}
                className="group p-6 rounded-2xl bg-card border border-border hover:border-primary/50 hover:shadow-lg hover:shadow-primary/5 transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                  <feature.icon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Input Methods Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                Control <span className="text-primary">Your Way</span>
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                Everyone is different. EyeControl App supports multiple input methods so you can choose
                what works best for you — or combine methods for the ultimate experience. No
                special hardware required to get started.
              </p>

              <div className="grid sm:grid-cols-2 gap-4">
                {inputMethods.map((method, index) => (
                  <motion.div
                    key={method.name}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center gap-3 p-3 rounded-lg bg-muted/50"
                  >
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <method.icon className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">{method.name}</p>
                      <p className="text-sm text-muted-foreground">{method.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="aspect-square rounded-3xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/20 flex items-center justify-center">
                <div className="text-center p-8">
                  <Accessibility className="h-24 w-24 text-primary mx-auto mb-6" />
                  <p className="text-2xl font-semibold">Accessible by Design</p>
                  <p className="text-muted-foreground mt-2">
                    Works with standard webcams, switches, or just your voice
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-primary/5">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <div className="grid grid-cols-2 gap-4">
                <div className="p-6 rounded-2xl bg-card border border-border text-center">
                  <Users className="h-8 w-8 text-primary mx-auto mb-3" />
                  <p className="text-3xl font-bold">1000+</p>
                  <p className="text-sm text-muted-foreground">Active Users</p>
                </div>
                <div className="p-6 rounded-2xl bg-card border border-border text-center">
                  <Star className="h-8 w-8 text-primary mx-auto mb-3" />
                  <p className="text-3xl font-bold">4.9</p>
                  <p className="text-sm text-muted-foreground">User Rating</p>
                </div>
                <div className="p-6 rounded-2xl bg-card border border-border text-center">
                  <Clock className="h-8 w-8 text-primary mx-auto mb-3" />
                  <p className="text-3xl font-bold">24/7</p>
                  <p className="text-sm text-muted-foreground">Support</p>
                </div>
                <div className="p-6 rounded-2xl bg-card border border-border text-center">
                  <Heart className="h-8 w-8 text-primary mx-auto mb-3" />
                  <p className="text-3xl font-bold">100%</p>
                  <p className="text-sm text-muted-foreground">Satisfaction</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                Why Choose <span className="text-primary">EyeControl App?</span>
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                We're committed to making assistive technology accessible, affordable, and
                effective for everyone who needs it.
              </p>

              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center gap-3"
                  >
                    <div className="w-6 h-6 rounded-full bg-green-500/10 flex items-center justify-center flex-shrink-0">
                      <Check className="h-4 w-4 text-green-500" />
                    </div>
                    <span>{benefit}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto text-center"
        >
          <div className="p-8 sm:p-12 rounded-3xl bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Ready to Experience <span className="text-primary">Freedom?</span>
            </h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Join thousands of users who have transformed their lives with EyeControl App. Start your free
              trial today — no credit card required.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button size="lg" onClick={() => navigate("/auth")} className="text-lg px-8">
                Start Free Trial
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => navigate("/pricing")}
                className="text-lg px-8"
              >
                View Pricing
              </Button>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t border-border">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <img
                src="/lovable-uploads/27b5c758-8df7-4797-90b4-1fa139f668f7.png"
                alt="EyeControl App Logo"
                className="h-6 w-auto"
              />
              <span className="font-semibold">EyeControl App</span>
            </div>
            <div className="flex flex-wrap justify-center gap-6 text-sm text-muted-foreground">
              <a href="/guide" className="hover:text-primary transition-colors">
                About
              </a>
              <a href="/pricing" className="hover:text-primary transition-colors">
                Pricing
              </a>
              <a href="/legal" className="hover:text-primary transition-colors">
                Legal
              </a>
              <a href="/legal/privacy" className="hover:text-primary transition-colors">
                Privacy
              </a>
            </div>
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} EyeControl App. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
